import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { FileItem, FolderItem } from '../hooks/useFiles';
import { apiGet } from '../lib/api';
import {
  useAllFolders,
  useBulkDeleteFiles,
  useBulkMoveFiles,
  useDeleteFile,
  useDeleteFolder,
  useMoveFile,
  useMoveFolder,
  useRenameFile,
  useRenameFolder,
  useShareFile,
} from '../hooks/useFiles';

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

function extAndColor(name: string, mime: string): { ext: string; bg: string; fg: string } {
  const m = /\.([a-zA-Z0-9]+)$/.exec(name || '');
  const ext = (m ? m[1] : (mime || '').split('/')[1] || 'file').slice(0, 4).toUpperCase();
  let bg = '#eef0f2', fg = '#4b5563';
  if (mime.startsWith('image/')) { bg = '#eef2ff'; fg = '#4338ca'; }
  else if (mime.startsWith('video/')) { bg = '#fdf2e9'; fg = '#b45309'; }
  else if (mime.startsWith('audio/')) { bg = '#ecfdf5'; fg = '#047857'; }
  else if (mime === 'application/pdf') { bg = '#fef2f2'; fg = '#b91c1c'; }
  return { ext, bg, fg };
}

const EXPIRE_OPTIONS = [
  { label: '7日間', days: 7 },
  { label: '30日間', days: 30 },
  { label: '無期限', days: null },
] as const;

const s = {
  header: { display: 'flex', alignItems: 'center', padding: '0.5rem 1rem', gap: '0.75rem', borderBottom: '1px solid #e5e7eb', background: '#fafafa', fontSize: '0.7rem', fontWeight: 600 as const, letterSpacing: '0.03em', color: '#9ca3af', textTransform: 'uppercase' as const } as const,
  row: { display: 'flex', alignItems: 'center', padding: '0.65rem 1rem', borderBottom: '1px solid #f3f4f6', gap: '0.75rem' } as const,
  folderIcon: { minWidth: '22px', width: '22px', height: '22px', borderRadius: '4px', background: '#f3f4f6', position: 'relative' as const, display: 'block' as const } as const,
  folderIconTab: { position: 'absolute' as const, left: '3px', top: '5px', width: '7px', height: '3px', background: '#9ca3af', borderRadius: '1px 1px 0 0' } as const,
  folderIconBody: { position: 'absolute' as const, left: '2px', top: '7px', width: '18px', height: '11px', background: '#d1d5db', borderRadius: '2px' } as const,
  name: { flex: 1, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const } as const,
  nameCell: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' as const, gap: '0.1rem' } as const,
  nameText: { fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const } as const,
  filePath: { fontSize: '0.72rem', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const } as const,
  meta: { fontSize: '0.75rem', color: '#9ca3af', minWidth: '60px', textAlign: 'right' as const } as const,
  actionsCell: { minWidth: '2rem', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' as const } as const,
  actionMenuWrap: { position: 'relative' as const } as const,
  kebabBtn: { width: '28px', height: '28px', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: '4px', display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', gap: '3px' } as const,
  kebabDot: { width: '3px', height: '3px', borderRadius: '50%', background: '#6b7280' } as const,
  actionMenuOverlay: { position: 'fixed' as const, inset: 0, zIndex: 30 } as const,
  actionMenuList: { position: 'fixed' as const, zIndex: 31, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: '150px', overflow: 'hidden', padding: '0.3rem 0' } as const,
  actionMenuItem: { padding: '0.5rem 0.9rem', fontSize: '0.8rem', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' as const } as const,
  actionMenuItemDanger: { padding: '0.5rem 0.9rem', fontSize: '0.8rem', color: '#b91c1c', cursor: 'pointer', whiteSpace: 'nowrap' as const } as const,
  btn: { padding: '0.2rem 0.45rem', fontSize: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '3px', background: '#fff', cursor: 'pointer', color: '#374151' } as const,
  delBtn: { padding: '0.2rem 0.45rem', fontSize: '0.75rem', border: '1px solid #f3b4b4', borderRadius: '3px', background: '#fff', cursor: 'pointer', color: '#b91c1c' } as const,
  editInput: { flex: 1, padding: '0.2rem 0.4rem', fontSize: '0.875rem', border: '1px solid #999999', borderRadius: '3px' } as const,
  empty: { padding: '3rem', textAlign: 'center' as const, color: '#9ca3af', fontSize: '0.875rem' } as const,
  overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 } as const,
  modal: { background: '#fff', borderRadius: '8px', padding: '1.5rem', width: '420px', maxWidth: '90vw', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } as const,
  modalTitle: { fontWeight: 600 as const, marginBottom: '1rem', color: '#111827', fontSize: '0.95rem' } as const,
  expireRow: { display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' } as const,
  expireBtn: (selected: boolean) => ({
    flex: 1, padding: '0.5rem', fontSize: '0.85rem', borderRadius: '4px', cursor: 'pointer',
    border: selected ? '2px solid #1a1a1a' : '1px solid #e5e7eb',
    background: selected ? '#ececec' : '#fff',
    color: selected ? '#1a1a1a' : '#374151',
    fontWeight: selected ? 600 : 400,
  } as const),
  createBtn: { width: '100%', padding: '0.55rem', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.9rem', cursor: 'pointer', marginBottom: '0.5rem', fontWeight: 500 as const } as const,
  urlRow: { display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' } as const,
  urlInput: { flex: 1, padding: '0.4rem 0.6rem', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '0.8rem', color: '#374151', background: '#f9fafb' } as const,
  copyBtn: { padding: '0.4rem 0.8rem', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' as const } as const,
  expireNote: { fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.75rem' } as const,
  closeBtn: { width: '100%', padding: '0.45rem', border: '1px solid #e5e7eb', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#6b7280' } as const,
  moveList: { maxHeight: '260px', overflowY: 'auto' as const, border: '1px solid #e5e7eb', borderRadius: '4px', marginBottom: '1rem' } as const,
  moveOption: (selected: boolean) => ({
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
    background: selected ? '#ececec' : '#fff',
    color: selected ? '#1a1a1a' : '#374151',
    fontWeight: selected ? 600 : 400,
    borderBottom: '1px solid #f3f4f6',
  } as const),
  moveEmpty: { padding: '0.75rem', fontSize: '0.8rem', color: '#9ca3af' } as const,
  checkboxCell: { minWidth: '1.25rem', width: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } as const,
  bulkBar: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 1rem', borderBottom: '1px solid #e5e7eb', background: '#f3f4f6', fontSize: '0.85rem', color: '#374151' } as const,
  bulkToggleBar: { display: 'flex', justifyContent: 'flex-end', padding: '0.5rem 1rem', borderBottom: '1px solid #e5e7eb' } as const,
};

// Maps each folder id to its full breadcrumb path, e.g. "aaa > bbb", so callers
// can show where a folder sits in the hierarchy instead of just its bare name.
export function buildFolderPathMap(all: FolderItem[]): Map<string, string> {
  const byId = new Map(all.map((f) => [f.id, f]));
  const cache = new Map<string, string>();

  const pathOf = (folder: FolderItem): string => {
    const cached = cache.get(folder.id);
    if (cached !== undefined) return cached;
    const names: string[] = [];
    let cur: FolderItem | undefined = folder;
    const seen = new Set<string>();
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      names.unshift(cur.name);
      cur = cur.parentFolderId ? byId.get(cur.parentFolderId) : undefined;
    }
    const path = names.join(' > ');
    cache.set(folder.id, path);
    return path;
  };

  const map = new Map<string, string>();
  for (const f of all) map.set(f.id, pathOf(f));
  return map;
}

interface FolderOption { id: string; path: string }

function buildFolderOptions(all: FolderItem[], excludeSubtreeOf?: string | string[]): FolderOption[] {
  const excludedIds = new Set<string>();
  const excludeRoots = excludeSubtreeOf ? (Array.isArray(excludeSubtreeOf) ? excludeSubtreeOf : [excludeSubtreeOf]) : [];
  if (excludeRoots.length > 0) {
    excludeRoots.forEach((id) => excludedIds.add(id));
    let changed = true;
    while (changed) {
      changed = false;
      for (const f of all) {
        if (f.parentFolderId && excludedIds.has(f.parentFolderId) && !excludedIds.has(f.id)) {
          excludedIds.add(f.id);
          changed = true;
        }
      }
    }
  }

  const pathMap = buildFolderPathMap(all);
  return all
    .filter((f) => !excludedIds.has(f.id))
    .map((f) => ({ id: f.id, path: pathMap.get(f.id) ?? f.name }))
    .sort((a, b) => a.path.localeCompare(b.path));
}

interface MoveModalProps {
  title: string;
  currentParentId: string | null;
  // A single folder id, or (for bulk-moving multiple folders at once) every
  // selected folder id — all of their subtrees are excluded as valid targets.
  excludeSubtreeOf?: string | string[];
  onMove: (targetFolderId: string | null) => Promise<unknown>;
  onClose: () => void;
  // Bulk moves have no single "current" folder to compare against (selected
  // files may come from different folders), so skip the no-op disable check.
  skipUnchangedCheck?: boolean;
}

function MoveModal({ title, currentParentId, excludeSubtreeOf, onMove, onClose, skipUnchangedCheck }: MoveModalProps): JSX.Element {
  const foldersQuery = useAllFolders();
  const [selected, setSelected] = useState<string | null>(currentParentId);
  const [submitting, setSubmitting] = useState(false);

  const options = buildFolderOptions(foldersQuery.data?.folders ?? [], excludeSubtreeOf);
  const unchanged = !skipUnchangedCheck && selected === currentParentId;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onMove(selected);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <p style={s.modalTitle}>{title}</p>
        <div style={s.moveList}>
          <div style={s.moveOption(selected === null)} onClick={() => setSelected(null)}>
            マイドライブ（ルート）
          </div>
          {foldersQuery.isLoading && <div style={s.moveEmpty}>読み込み中…</div>}
          {options.map((o) => (
            <div
              key={o.id}
              style={s.moveOption(selected === o.id)}
              onClick={() => setSelected(o.id)}
            >
              {o.path}
            </div>
          ))}
        </div>
        <button style={s.createBtn} onClick={handleConfirm} disabled={submitting || unchanged}>
          {submitting ? '移動中…' : 'ここに移動'}
        </button>
        <button style={s.closeBtn} onClick={onClose}>キャンセル</button>
      </div>
    </div>
  );
}

interface ActionMenuItem { label: string; onClick: () => void; danger?: boolean }

function ActionMenu({ items }: { items: ActionMenuItem[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 2, right: window.innerWidth - rect.right });
    }
    setOpen((o) => !o);
  };

  return (
    <div style={s.actionMenuWrap}>
      <button
        ref={btnRef}
        type="button"
        className="kebab-btn"
        style={s.kebabBtn}
        aria-label="操作メニュー"
        onClick={toggle}
      >
        <span style={s.kebabDot} />
        <span style={s.kebabDot} />
        <span style={s.kebabDot} />
      </button>

      {open && pos && createPortal(
        <>
          <div style={s.actionMenuOverlay} onClick={() => setOpen(false)} />
          <div style={{ ...s.actionMenuList, top: pos.top, right: pos.right }} onClick={(e) => e.stopPropagation()}>
            {items.map((item) => (
              <div
                key={item.label}
                className="action-menu-item"
                style={item.danger ? s.actionMenuItemDanger : s.actionMenuItem}
                onClick={() => { setOpen(false); item.onClick(); }}
              >
                {item.label}
              </div>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

interface FolderRowProps {
  folder: FolderItem;
  onClick: (id: string) => void;
  bulkMode: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
}

function FolderRow({ folder, onClick, bulkMode, selected, onToggleSelect }: FolderRowProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const rename = useRenameFolder();
  const del = useDeleteFolder();
  const move = useMoveFolder();

  const save = async () => {
    if (name.trim() && name !== folder.name) await rename.mutateAsync({ id: folder.id, name: name.trim() });
    setEditing(false);
  };

  return (
    <div style={s.row} className="file-row">
      <span style={s.checkboxCell}>
        {bulkMode && (
          <input type="checkbox" checked={selected} onChange={() => onToggleSelect(folder.id)} />
        )}
      </span>
      <span style={s.folderIcon}>
        <span style={s.folderIconTab} />
        <span style={s.folderIconBody} />
      </span>
      {editing ? (
        <>
          <input style={s.editInput} value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setName(folder.name); setEditing(false); } }}
            autoFocus />
          <button style={s.btn} onClick={save}>保存</button>
          <button style={s.btn} onClick={() => { setName(folder.name); setEditing(false); }}>キャンセル</button>
        </>
      ) : (
        <>
          <span style={{ ...s.name, cursor: 'pointer', color: '#1a1a1a', fontWeight: 600 }} onClick={() => onClick(folder.id)}>{folder.name}</span>
          <span style={s.meta}>—</span>
          <div style={s.actionsCell}>
            <ActionMenu
              items={[
                { label: '移動', onClick: () => setMoveModalOpen(true) },
                { label: '名前を変更', onClick: () => setEditing(true) },
                {
                  label: '削除',
                  danger: true,
                  onClick: () => { if (window.confirm(`フォルダ「${folder.name}」とその中身をすべて削除しますか？`)) del.mutate(folder.id); },
                },
              ]}
            />
          </div>
        </>
      )}

      {moveModalOpen && (
        <MoveModal
          title={`「${folder.name}」を移動`}
          currentParentId={folder.parentFolderId}
          excludeSubtreeOf={folder.id}
          onMove={(targetId) => move.mutateAsync({ id: folder.id, parentFolderId: targetId })}
          onClose={() => setMoveModalOpen(false)}
        />
      )}
    </div>
  );
}

type ModalPhase = 'select' | 'url';

interface FileRowProps {
  file: FileItem;
  bulkMode: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  folderPath?: string;
}

function FileRow({ file, bulkMode, selected, onToggleSelect, folderPath }: FileRowProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(file.name);
  const [phase, setPhase] = useState<ModalPhase>('select');
  const [modalOpen, setModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number | null>(7);
  const [modalUrl, setModalUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const rename = useRenameFile();
  const del = useDeleteFile();
  const share = useShareFile();
  const move = useMoveFile();

  const mime = file.mimeType ?? '';
  const { ext, bg, fg } = extAndColor(file.name, mime);

  const save = async () => {
    if (name.trim() && name !== file.name) await rename.mutateAsync({ id: file.id, name: name.trim() });
    setEditing(false);
  };

  const openModal = () => {
    setSelectedDays(7);
    setPhase('select');
    setModalOpen(true);
  };

  const handleCreate = async () => {
    const expireIso = selectedDays
      ? new Date(Date.now() + selectedDays * 24 * 60 * 60 * 1000).toISOString()
      : null;
    const res = await share.mutateAsync({ id: file.id, expiresAt: expireIso });
    if (res.success && res.data?.shareToken) {
      setModalUrl(`${window.location.origin}/share/${res.data.shareToken}`);
      setExpiresAt(res.data.expiresAt ?? null);
      setPhase('url');
    }
  };

  const handleCopy = async () => {
    if (!modalUrl) return;
    await navigator.clipboard.writeText(modalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalUrl(null);
    setCopied(false);
  };

  return (
    <>
      <div style={s.row} className="file-row">
        <span style={s.checkboxCell}>
          {bulkMode && (
            <input type="checkbox" checked={selected} onChange={() => onToggleSelect(file.id)} />
          )}
        </span>
        <span style={{
          minWidth: '22px', width: '22px', height: '22px', padding: 0, borderRadius: '4px',
          background: bg, color: fg, fontFamily: 'ui-monospace, "SF Mono", monospace',
          fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.01em',
          display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        }}>{ext}</span>
        {editing ? (
          <>
            <input style={s.editInput} value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setName(file.name); setEditing(false); } }}
              autoFocus />
            <button style={s.btn} onClick={save}>保存</button>
            <button style={s.btn} onClick={() => { setName(file.name); setEditing(false); }}>キャンセル</button>
          </>
        ) : (
          <>
            <span style={s.nameCell}>
              <span style={s.nameText}>{file.name}</span>
              {folderPath !== undefined && <span style={s.filePath}>{folderPath}</span>}
            </span>
            <span style={s.meta}>{formatBytes(file.sizeBytes)}</span>
            <div style={s.actionsCell}>
              <ActionMenu
                items={[
                  {
                    label: 'ダウンロード',
                    onClick: async () => {
                      const res = await apiGet<{ downloadUrl: string }>(`/files/${file.id}/download`);
                      if (res.success && res.data?.downloadUrl) {
                        const a = document.createElement('a');
                        a.href = res.data.downloadUrl;
                        a.click();
                      }
                    },
                  },
                  { label: '共有', onClick: openModal },
                  { label: '移動', onClick: () => setMoveModalOpen(true) },
                  { label: '名前を変更', onClick: () => setEditing(true) },
                  {
                    label: '削除',
                    danger: true,
                    onClick: () => { if (window.confirm(`「${file.name}」を削除しますか？`)) del.mutate(file.id); },
                  },
                ]}
              />
            </div>
          </>
        )}
      </div>

      {moveModalOpen && (
        <MoveModal
          title={`「${file.name}」を移動`}
          currentParentId={file.folderId}
          onMove={(targetId) => move.mutateAsync({ id: file.id, folderId: targetId })}
          onClose={() => setMoveModalOpen(false)}
        />
      )}

      {modalOpen && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            {phase === 'select' ? (
              <>
                <p style={s.modalTitle}>共有リンクを作成</p>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>有効期限</p>
                <div style={s.expireRow}>
                  {EXPIRE_OPTIONS.map((opt) => (
                    <button
                      key={opt.label}
                      style={s.expireBtn(selectedDays === opt.days)}
                      onClick={() => setSelectedDays(opt.days)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <button style={s.createBtn} onClick={handleCreate} disabled={share.isPending}>
                  {share.isPending ? '作成中…' : 'リンクを作成'}
                </button>
                <button style={s.closeBtn} onClick={closeModal}>キャンセル</button>
              </>
            ) : (
              <>
                <p style={s.modalTitle}>共有リンク</p>
                <div style={s.urlRow}>
                  <input style={s.urlInput} readOnly value={modalUrl ?? ''} onFocus={(e) => e.target.select()} />
                  <button style={s.copyBtn} onClick={handleCopy}>
                    {copied ? 'コピー済み' : 'コピー'}
                  </button>
                </div>
                <p style={s.expireNote}>
                  {expiresAt ? `有効期限: ${formatDate(expiresAt)}` : '有効期限: 無期限'}
                </p>
                <button style={s.closeBtn} onClick={closeModal}>閉じる</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

interface FileListProps {
  folders: FolderItem[];
  files: FileItem[];
  loading: boolean;
  onFolderOpen: (id: string) => void;
  // When set, each file row shows the folder path it lives in (e.g. "aaa > bbb").
  // Passed only in search results, where files can come from any folder.
  folderPathMap?: Map<string, string>;
}

export function FileList({ folders, files, loading, onFolderOpen, folderPathMap }: FileListProps): JSX.Element {
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [bulkMoveModalOpen, setBulkMoveModalOpen] = useState(false);
  const bulkDelete = useBulkDeleteFiles();
  const bulkMove = useBulkMoveFiles();
  const moveFolder = useMoveFolder();
  const deleteFolder = useDeleteFolder();

  if (loading) return <div style={s.empty}>読み込み中…</div>;
  if (folders.length === 0 && files.length === 0) {
    return <div style={s.empty}>このフォルダは空です。ファイルをアップロードするか、フォルダを作成してください。</div>;
  }

  const totalSelectable = files.length + folders.length;
  const totalSelected = selected.size + selectedFolders.size;
  const allSelected = totalSelectable > 0
    && files.every((f) => selected.has(f.id))
    && folders.every((fo) => selectedFolders.has(fo.id));

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectFolder = (id: string) => {
    setSelectedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
      setSelectedFolders(new Set());
    } else {
      setSelected(new Set(files.map((f) => f.id)));
      setSelectedFolders(new Set(folders.map((f) => f.id)));
    }
  };

  const clearSelection = () => {
    setSelected(new Set());
    setSelectedFolders(new Set());
  };

  const toggleBulkMode = () => {
    setBulkMode((prev) => !prev);
    clearSelection();
  };

  const handleBulkDelete = async () => {
    if (!totalSelected) return;
    const warning = selectedFolders.size > 0
      ? `選択した${totalSelected}件を削除しますか？フォルダを含む場合、その中身もすべて削除されます。`
      : `選択した${totalSelected}件のファイルを削除しますか？`;
    if (!window.confirm(warning)) return;
    const tasks: Promise<unknown>[] = [];
    if (selected.size > 0) tasks.push(bulkDelete.mutateAsync(Array.from(selected)));
    for (const id of selectedFolders) tasks.push(deleteFolder.mutateAsync(id));
    await Promise.all(tasks);
    clearSelection();
  };

  const handleBulkMove = async (folderId: string | null) => {
    const tasks: Promise<unknown>[] = [];
    if (selected.size > 0) {
      tasks.push(bulkMove.mutateAsync({ fileIds: Array.from(selected), folderId }));
    }
    for (const id of selectedFolders) {
      tasks.push(moveFolder.mutateAsync({ id, parentFolderId: folderId }));
    }
    await Promise.all(tasks);
    clearSelection();
  };

  return (
    <div>
      {bulkMode ? (
        <div style={s.bulkBar}>
          {totalSelected > 0 ? (
            <>
              <span>{totalSelected} 件選択中</span>
              <button style={s.btn} onClick={clearSelection}>選択解除</button>
              <button style={s.btn} onClick={() => setBulkMoveModalOpen(true)}>移動</button>
              <button style={s.delBtn} onClick={handleBulkDelete} disabled={bulkDelete.isPending || deleteFolder.isPending || totalSelected === 0}>
                {(bulkDelete.isPending || deleteFolder.isPending) ? '削除中…' : '削除'}
              </button>
            </>
          ) : (
            <span style={{ color: '#9ca3af' }}>ファイル・フォルダを選択してください</span>
          )}
          <button style={{ ...s.btn, marginLeft: 'auto' }} onClick={toggleBulkMode}>完了</button>
        </div>
      ) : (
        <div style={s.bulkToggleBar}>
          <button style={s.btn} onClick={toggleBulkMode}>一括操作</button>
        </div>
      )}

      {bulkMoveModalOpen && (
        <MoveModal
          title={`選択した${totalSelected}件を移動`}
          currentParentId={null}
          skipUnchangedCheck
          excludeSubtreeOf={Array.from(selectedFolders)}
          onMove={handleBulkMove}
          onClose={() => setBulkMoveModalOpen(false)}
        />
      )}
      <div style={s.header}>
        <span style={s.checkboxCell}>
          {bulkMode && totalSelectable > 0 && (
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
          )}
        </span>
        <span style={{ minWidth: '1.5rem' }} />
        <span style={{ flex: 1 }}>名前</span>
        <span style={{ minWidth: '60px', textAlign: 'right' as const }}>サイズ</span>
        <span style={{ minWidth: '2rem', flexShrink: 0, textAlign: 'right' as const }}>操作</span>
      </div>
      {folders.map((f) => (
        <FolderRow
          key={f.id}
          folder={f}
          onClick={onFolderOpen}
          bulkMode={bulkMode}
          selected={selectedFolders.has(f.id)}
          onToggleSelect={toggleSelectFolder}
        />
      ))}
      {files.map((f) => (
        <FileRow
          key={f.id}
          file={f}
          bulkMode={bulkMode}
          selected={selected.has(f.id)}
          onToggleSelect={toggleSelect}
          folderPath={folderPathMap ? (f.folderId ? (folderPathMap.get(f.folderId) ?? '') : 'マイドライブ') : undefined}
        />
      ))}
    </div>
  );
}
